import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Key, CheckCircle, ShieldCheck, ListChecks, ChatCircleDots, ArrowRight, CaretDown } from "@phosphor-icons/react";

export default function Activate() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [keyInput, setKeyInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const activate = async () => {
    if (!keyInput.trim()) return;
    setBusy(true);
    try {
      await api.post("/activate", { key: keyInput.trim() });
      toast.success("Key activated — it's now locked to your email");
      refresh && (await refresh());
      navigate("/plan");
    } catch (err) {
      toast.error(apiError(err), { duration: 14000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] text-zinc-100">
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 pt-28 pb-20">
        <p className="font-mono-p text-xs tracking-[0.3em] uppercase text-cyan-400 mb-2">// Activation</p>
        <h1 className="font-mono-p font-bold uppercase tracking-tight text-3xl border-b border-zinc-800 pb-8 flex items-center gap-3">
          <Key size={28} weight="duotone" className="text-cyan-400" /> Activate Your AI iD Card
        </h1>

        {user?.active_key && (
          <div className="mt-8 border border-green-500/30 bg-green-500/5 p-5" data-testid="already-active">
            <div className="flex items-center gap-2 text-green-400">
              <CheckCircle size={18} weight="fill" />
              <span className="font-mono-p text-xs uppercase tracking-widest">Key Active</span>
            </div>
            <p className="mt-2 text-sm text-zinc-400">
              Activated under <span className="text-cyan-300">{user.plan_name || "your plan"}</span>, locked to <span className="text-zinc-200">{user.email}</span>.
              <button onClick={() => navigate("/plan")} data-testid="go-to-plan-btn" className="ml-2 text-cyan-400 hover:underline">View Plan & Keys</button>
            </p>
          </div>
        )}

        <p className="mt-8 text-sm text-zinc-400 max-w-xl">Choose how you'd like to build and activate your AI iD Card:</p>

        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {/* Self-Guided Survey */}
          <button
            data-testid="option-self-guided"
            onClick={() => navigate("/build")}
            className="group text-left border border-zinc-800 bg-[#090A0C] p-7 hover:border-cyan-400/60 transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-cyan-400/10 border border-cyan-400/30 flex items-center justify-center">
              <ListChecks size={24} weight="duotone" className="text-cyan-400" />
            </div>
            <h2 className="mt-5 font-mono-p font-bold uppercase tracking-tight text-lg">Self-Guided Survey</h2>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">
              Answer a short guided form and build your AI iD Card yourself, step by step. Best if you have your key details ready.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-cyan-400">
              Start the survey <ArrowRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </span>
          </button>

          {/* Live Chat Support Walkthrough */}
          <button
            data-testid="option-live-chat"
            onClick={() => navigate("/support", { state: { flow: "activation" } })}
            className="group text-left border border-zinc-800 bg-[#090A0C] p-7 hover:border-green-400/60 transition-colors"
          >
            <div className="w-12 h-12 rounded-full bg-green-400/10 border border-green-400/30 flex items-center justify-center">
              <ChatCircleDots size={24} weight="duotone" className="text-green-400" />
            </div>
            <h2 className="mt-5 font-mono-p font-bold uppercase tracking-tight text-lg">Live Chat Support Walkthrough</h2>
            <p className="mt-2 text-sm text-zinc-500 leading-relaxed">
              Get walked through activation in live chat. Our team (or AI specialist) guides you using your member number, key and registered email.
            </p>
            <span className="mt-5 inline-flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-green-400">
              Open live chat <ArrowRight size={15} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>

        {/* Advanced: activate a purchased PRID license key directly */}
        {!user?.active_key && (
          <div className="mt-10 border-t border-zinc-900 pt-5">
            <button
              data-testid="toggle-prid"
              onClick={() => setShowKey((v) => !v)}
              className="flex items-center gap-2 font-mono-p text-xs uppercase tracking-widest text-zinc-500 hover:text-cyan-400 transition-colors"
            >
              <CaretDown size={14} className={`transition-transform ${showKey ? "rotate-180" : ""}`} />
              Have a purchased license key? Activate it directly
            </button>
            {showKey && (
              <div className="mt-4 border border-zinc-800 bg-[#090A0C] p-6">
                <p className="text-sm text-zinc-400 mb-4 flex items-start gap-2">
                  <ShieldCheck size={18} className="text-cyan-400 mt-0.5 shrink-0" />
                  Enter the license key you purchased. It locks permanently to your email and can never be reused.
                </p>
                <div className="flex flex-col sm:flex-row gap-3">
                  <input
                    data-testid="activate-key-input"
                    value={keyInput}
                    onChange={(e) => setKeyInput(e.target.value.toUpperCase())}
                    onKeyDown={(e) => e.key === "Enter" && activate()}
                    placeholder="PRID-XXXX-XXXX-XXXX"
                    className="flex-1 bg-black border border-zinc-800 px-4 py-3 text-cyan-300 font-mono-p text-sm tracking-wider focus:border-cyan-400/60 focus:outline-none transition-colors"
                  />
                  <button
                    data-testid="activate-key-btn"
                    onClick={activate}
                    disabled={busy || !keyInput.trim()}
                    className="font-mono-p text-sm uppercase tracking-widest bg-cyan-400 text-black px-6 py-3 font-bold hover:bg-cyan-300 transition-colors disabled:opacity-50"
                  >
                    {busy ? "Activating…" : "Activate"}
                  </button>
                </div>
                <p className="mt-4 text-xs text-zinc-600">
                  Don't have a key? <button onClick={() => navigate("/store")} data-testid="go-store-link" className="text-cyan-400 hover:underline">Get one from the Store</button>.
                </p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
